import { useState } from "react";

import { Link, useLocation } from "@remix-run/react";

import { Button, Description } from "~/components";
import { RadioWithContentGroupItem } from "~/components/form";
import {
    QuestionContainer,
    QuestionInfoContainer,
    QuestionInfoImage,
    QuestionInfoImages,
    QuestionInfoSubtitle,
    QuestionInfoTitle,
    QuestionnaireContainer,
    QuestionnaireHeading,
    QuestionnaireHeadingContainer,
    QuestionnaireSubheading,
    flowRadioClassName,
    BrooklawnAssuranceModal,
} from "~/components/questionnaire/components";
import { RadioGroup } from "~/components/ui/radio-group";
import { AboveGroundIcon, BelowGroundIcon, CompanionIcon, FamilyIcon, SingleIcon } from "~/icons";
import { useTrackLinks } from "~/utils/analytics";
import data from "~/data";

function PreNeedImages({ images }: { images?: string[] | [] }) {
    const length = images?.length;

    let className = "";
    if (length === 3) {
        className = "col-span-2 w-full h-52 object-cover";
    }

    return (
        <QuestionInfoImages>
            {images?.map((image: string, index: number) => {
                if (length === 3 && index === 0) {
                    return <QuestionInfoImage key={index} src={image} className={className} />
                }

                return <QuestionInfoImage key={index} src={image} />
            })}
        </QuestionInfoImages>
    );
}

export function GetStarted({ send }) {
    function handleOnClick({ type }: { type: string | null }) {
        send({ type, analytics: ["1. Plan Ahead for Peace of Mind"] });
    }

    return (
        <QuestionnaireContainer className="md:min-h-[710px]">
            <QuestionInfoContainer>
                <QuestionInfoTitle>Create a Lasting Tribute with Convenience</QuestionInfoTitle>
                <QuestionInfoSubtitle className="md:flex-1">
                    Discover and reserve a final resting place online, made easy.
                </QuestionInfoSubtitle>
                <PreNeedImages
                    images={[
                        "https://res.cloudinary.com/dcam5dgpl/image/upload/v1718967320/image2_ifmyrk.png",
                        "https://res.cloudinary.com/dcam5dgpl/image/upload/v1718967320/image_wzpriy.png",
                    ]}
                />
            </QuestionInfoContainer>

            <QuestionContainer>
                <div className="flex flex-col flex-1 justify-center gap-6 h-full my-auto">
                    <QuestionnaireHeading>Plan Ahead for Peace of Mind</QuestionnaireHeading>
                    <p className="text-center">
                        See how a few straightforward steps can ease the emotional and financial load on your family.
                        We'll be with you throughout the process, customizing cemetery and memorial services to your
                        wishes.
                    </p>
                    <Button className="md:mt-4" onClick={() => handleOnClick({ type: "NEXT" })}>
                        Plan Now
                    </Button>
                </div>
            </QuestionContainer>
        </QuestionnaireContainer>
    );
}

export function GetStartedBrooklawn({ send }) {
    function handleOnClick({ type }: { type: string | null }) {
        send({ type, analytics: ["1. Plan Ahead for Peace of Mind"] });
    }

    return (
        <QuestionnaireContainer className="md:min-h-[710px]">
            <QuestionInfoContainer>
                <QuestionInfoTitle>Create a Lasting Tribute with Convenience</QuestionInfoTitle>
                <QuestionInfoSubtitle className="md:flex-1">
                    Discover and reserve a final resting place online, made easy.
                </QuestionInfoSubtitle>
                <BrooklawnAssuranceModal />
                <PreNeedImages
                    images={data?.flow?.one || []}
                />
            </QuestionInfoContainer>

            <QuestionContainer>
                <div className="flex flex-col flex-1 justify-center gap-6 h-full my-auto">
                    <QuestionnaireHeading>Plan Ahead for Peace of Mind</QuestionnaireHeading>
                    <p className="text-center">
                        See how a few straightforward steps can ease the emotional and financial load on your family.
                        We'll be with you throughout the process, customizing cemetery and memorial services to your
                        wishes.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <button className="relative flex flex-col items-center justify-center gap-6 bg-white p-6 rounded-lg shadow-lg !min-h-[128px] md:min-h-[auto] group overflow-hidden"
                            onClick={() => handleOnClick({ type: "NEXT" })}>
                            <div className="absolute top-0 left-0 h-1.5 w-0 group-hover:w-full bg-primary transition-all duration-300" />
                            <img
                                src="https://res.cloudinary.com/dcam5dgpl/image/upload/v1718964168/image_32_lwkxml.svg"
                                alt="immediate need"
                                className="w-10 h-10 md:w-16 md:h-16"
                            />
                            <section className="flex flex-col justify-center gap-2 h-full text-center">
                                <h2 className="font-bold md:text-xl">Immediate Need</h2>
                                <p>A loved one has passed or is about to pass</p>
                            </section>
                        </button>
                        <button className="relative flex flex-col items-center justify-center gap-6 bg-white p-6 rounded-lg shadow-lg !min-h-[128px] md:min-h-[auto] group overflow-hidden"
                            onClick={() => handleOnClick({ type: "NEXT" })}>
                            <div className="absolute top-0 left-0 h-1.5 w-0 group-hover:w-full bg-primary transition-all duration-300" />
                            <img
                                src="https://res.cloudinary.com/dcam5dgpl/image/upload/v1718964168/image_31_nxacux.svg"
                                alt="immediate need"
                                className="w-10 h-10 md:w-16 md:h-16"
                            />
                            <section className="flex flex-col justify-center gap-2 h-full text-center">
                                <h2 className="font-bold md:text-xl">Plan For The Future</h2>
                                <p>Simple Memorial Planning</p>
                            </section>
                        </button>
                    </div>
                </div>
            </QuestionContainer>
        </QuestionnaireContainer>
    );
}

export function ConsideringCremation({ send, link }) {
    function handleOnClick({ type }: { type: string | null }) {
        send({
            type,
            analytics: ["3. Considering cremation options for yourself or a loved one?", { value: type || "" }],
        });
    }

    useTrackLinks("#cremation-not-sure", "3. Considering cremation options for yourself or a loved one?", {
        value: "Not Sure",
    });

    return (
        <QuestionnaireContainer className="md:min-h-[710px]">
            <QuestionInfoContainer>
                <QuestionInfoTitle>Compassionate online memorial planning</QuestionInfoTitle>
                <QuestionInfoSubtitle className="md:flex-1">
                    Discover peaceful resting places for yourself or a loved one online, with compassionate assistance
                    always available.
                </QuestionInfoSubtitle>
                <PreNeedImages
                    images={data?.flow?.three || []}
                />
            </QuestionInfoContainer>

            <QuestionContainer send={send}>
                <QuestionnaireHeading>Considering cremation options for yourself or a loved one?</QuestionnaireHeading>
                <QuestionInfoSubtitle />
                <div className="flex flex-col gap-3 w-full justify-center items-center">
                    <section className="self-stretch flex flex-col-reverse md:flex-row gap-3">
                        <Button className="w-full" variant="outline" onClick={() => handleOnClick({ type: "BURIED" })}>
                            No
                        </Button>
                        <Button className="w-full" onClick={() => handleOnClick({ type: "CREMATED" })}>
                            Yes
                        </Button>
                    </section>
                    <Link
                        id="cremation-not-sure"
                        // FIX: Links to highlighted text - should we ask Greenwood to hyperlink ?
                        to={link}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button variant="action">Not Sure</Button>
                    </Link>
                    <Link id="explore-space-options-now" to="./map">
                        <Button className="" variant="action">
                            Browse Available Spaces
                        </Button>
                    </Link>
                </div>
            </QuestionContainer>
        </QuestionnaireContainer>
    );
}

export function GroundLevel({ send }) {
    const [type, setType] = useState<"ABOVE_GROUND" | "BELOW_GROUND" | null>(null);

    const { pathname } = useLocation();
    const heading =
        pathname === "/immediate-need"
            ? "What type of burial are you looking for to honor your loved one?"
            : "What type of burial are you looking for?";

    function handleOnClick({ type }: { type: string | null }) {
        send({ type, analytics: [`4. ${heading}`, { value: type || "" }] });
    }

    useTrackLinks("#explore-space-options-now", `4. ${heading}`, {
        value: "Browse Available Spaces",
    });

    return (
        <QuestionnaireContainer className="md:min-h-[710px]">
            <QuestionInfoContainer>
                <QuestionInfoTitle>Understanding Your Choices: Above Ground vs. Below Ground</QuestionInfoTitle>
                <QuestionInfoSubtitle className="md:flex-1">
                    Choosing the right resting place is a step towards honoring a life well-lived. Above ground options,
                    like mausoleums and niches, offer a serene and accessible way to honor your loved one. Below ground
                    options, such as burial plots and lawn crypts, provide a timeless and traditional tribute. Each
                    option offers a respectful and loving commemoration.
                </QuestionInfoSubtitle>
                <PreNeedImages
                    images={data?.flow?.four || []}
                />
            </QuestionInfoContainer>
            <QuestionContainer send={send}>
                <QuestionnaireHeadingContainer>
                    <QuestionnaireHeading>{heading}</QuestionnaireHeading>
                    <QuestionnaireSubheading>Please select an option to continue</QuestionnaireSubheading>
                </QuestionnaireHeadingContainer>
                <QuestionInfoSubtitle />
                <RadioGroup className="w-full gap-4 grid md:grid-cols-2" onValueChange={setType}>
                    <RadioWithContentGroupItem
                        className={flowRadioClassName}
                        value="ABOVE_GROUND"
                        title={<AboveGroundIcon className="w-12 h-12" />}
                        description={<Description>Above Ground</Description>}
                    />
                    <RadioWithContentGroupItem
                        className={flowRadioClassName}
                        value="BELOW_GROUND"
                        title={<BelowGroundIcon className="w-12 h-12" />}
                        description={<Description>Below Ground</Description>}
                    />
                </RadioGroup>
                <div className="flex flex-col gap-3 w-full justify-center items-center">
                    <Button
                        className="w-full"
                        disabled={!type}
                        variant={!type ? "disabled" : "default"}
                        onClick={() => handleOnClick({ type })}
                    >
                        Continue
                    </Button>
                    <Link id="explore-space-options-now" className="" to="./map">
                        <Button className="" variant="action">
                            Browse Available Spaces
                        </Button>
                    </Link>
                </div>
            </QuestionContainer>
        </QuestionnaireContainer>
    );
}

export function PurchaseForWho({ send }) {
    const [type, setType] = useState<"MYSELF" | "ME_AND_PARTNER" | "FAMILY_MEMBERS" | null>(null);

    function handleOnClick({ type }: { type: string | null }) {
        send({ type, analytics: ["2. Who is this purchase for?", { value: type || "" }] });
    }

    useTrackLinks("#explore-space-options-now", "2. Who is this purchase for?", {
        value: "Browse Available Spaces",
    });

    return (
        <QuestionnaireContainer className="md:min-h-[710px]">
            <QuestionInfoContainer>
                <QuestionInfoTitle>Personalize Your Memorial Planning</QuestionInfoTitle>
                <QuestionInfoSubtitle className="md:flex-1">
                    Plan for yourself, together with a partner, or for those you cherish. We're here to support you
                    every step of the way.
                </QuestionInfoSubtitle>
                <PreNeedImages
                    images={data?.flow?.two || []}
                />
            </QuestionInfoContainer>
            <QuestionContainer send={send}>
                <QuestionnaireHeadingContainer>
                    <QuestionnaireHeading>Who is this purchase for?</QuestionnaireHeading>
                    <QuestionnaireSubheading>Please select an option to continue</QuestionnaireSubheading>
                </QuestionnaireHeadingContainer>
                <QuestionInfoSubtitle />
                <RadioGroup className="grid md:grid-cols-3 w-full" onValueChange={setType}>
                    <RadioWithContentGroupItem
                        className={flowRadioClassName}
                        value="MYSELF"
                        title={<SingleIcon className="w-10 h-10" />}
                        description={<Description>Myself</Description>}
                    />
                    <RadioWithContentGroupItem
                        className={flowRadioClassName}
                        value="ME_AND_PARTNER"
                        title={<CompanionIcon className="w-10 h-10" />}
                        description={<Description>My partner and me</Description>}
                    />
                    <RadioWithContentGroupItem
                        className={flowRadioClassName}
                        value="FAMILY_MEMBERS"
                        title={<FamilyIcon className="w-10 h-10" />}
                        description={<Description>One or more loved ones</Description>}
                    />
                </RadioGroup>
                <div className="flex flex-col gap-3 w-full justify-center items-center">
                    <Button
                        className="w-full"
                        disabled={!type}
                        variant={!type ? "disabled" : "default"}
                        onClick={() => handleOnClick({ type })}
                    >
                        Continue
                    </Button>
                    <Link id="explore-space-options-now" className="" to="./map">
                        <Button className="" variant="action">
                            Browse Available Spaces
                        </Button>
                    </Link>
                </div>
            </QuestionContainer>
        </QuestionnaireContainer>
    );
}
